import React from 'react';
import './Terms.css';

const Troubleshooting: React.FC = () => (
  <div className="terms-page">
    <div className="terms-content">
      <h1>Troubleshooting</h1>
      <h2>Heal Troubleshooting Guide</h2>
      <p>
        Most things fix in seconds. Work through the quick steps below first — they solve the majority of issues.
      </p>

      <h2>Start here: five quick steps</h2>
      <ol>
        <li>
          <strong>Close Heal and reopen it</strong> — swipe up from the bottom of your screen (or double-tap home), swipe Heal away, then reopen.
        </li>
        <li>
          <strong>Check for app updates</strong> — open the App Store, tap your profile, scroll to find pending updates. Update Heal if it&apos;s there.
        </li>
        <li>
          <strong>Restart your phone</strong> — a full power cycle clears most lingering issues, especially anything Bluetooth-related.
        </li>
        <li>
          <strong>Sign out, sign back in</strong> — Profile, then Settings, then Sign Out. Sign back in with your usual login. Your data stays safe in the cloud.
        </li>
        <li>
          <strong>Delete and reinstall</strong> — hold the Heal icon, tap Remove App, then reinstall from the App Store. Your account and data are saved server-side — nothing is lost.
        </li>
      </ol>

      <h2>The App Itself</h2>

      <h3>Heal won&apos;t open, or opens to a blank screen</h3>
      <ol>
        <li>
          <strong>Force-quit and reopen</strong> — swipe up to multitasking, swipe Heal away, then reopen.
        </li>
        <li>
          <strong>Restart your phone</strong> — hold the side and volume buttons until &ldquo;slide to power off&rdquo; appears.
        </li>
        <li>
          <strong>Check for an iOS update</strong> — iOS Settings, then General, then Software Update. Older iOS versions sometimes hit edge cases.
        </li>
        <li>
          <strong>Check storage</strong> — iOS Settings, then General, then iPhone Storage. If you&apos;re under 1 GB free, apps can fail to launch.
        </li>
        <li>
          <strong>Delete and reinstall</strong> — hold the Heal icon, Remove App, Delete. Then reinstall from the App Store. Your account stays.
        </li>
      </ol>

      <h3>The app crashes mid-session</h3>
      <ol>
        <li>
          <strong>Reopen and resume</strong> — your session is saved — sessions auto-save every 30 seconds. You&apos;ll pick up where you left off.
        </li>
        <li>
          <strong>Close any other audio apps</strong> — streaming apps competing for audio output can cause crashes. Close Spotify, Apple Music, Podcasts, etc.
        </li>
        <li>
          <strong>Restart the phone</strong> — especially if this happens repeatedly — there&apos;s usually a memory issue in iOS that a restart clears.
        </li>
        <li>
          <strong>Check for updates and reinstall if it keeps happening</strong> — App Store, your profile, look for Heal under Available Updates. If the issue persists after updating, delete and reinstall.
        </li>
      </ol>

      <h3>Heal feels slow or laggy</h3>
      <ol>
        <li>
          <strong>Close background apps</strong> — swipe up and clear apps you&apos;re not using — especially ones streaming audio or video.
        </li>
        <li>
          <strong>Check your connection</strong> — Heal works offline for most things, but content loading needs Wi-Fi or cellular. Try toggling airplane mode on and off.
        </li>
        <li>
          <strong>Restart the phone</strong> — standard fix for slowness across any app.
        </li>
      </ol>

      <h2>Watch &amp; wearable</h2>

      <h3>HRV isn&apos;t showing in Heal</h3>
      <ol>
        <li>
          <strong>Open your wearable&apos;s own app first</strong> — Whoop, Oura, Apple Health — does today&apos;s HRV show there? If not, the issue is with the wearable&apos;s sync, not Heal.
        </li>
        <li>
          <strong>Pull down to refresh in Heal</strong> — on the Insights screen, pull down to manually trigger a sync.
        </li>
        <li>
          <strong>Check the wearable is connected</strong> — Profile, then Settings, then Wearables. If it shows as disconnected, tap to reconnect.
        </li>
        <li>
          <strong>Check HealthKit permissions (Apple Watch users)</strong> — iOS Settings, then Health, then Data Access &amp; Devices, then Heal. Make sure HRV is toggled on.
        </li>
        <li>
          <strong>Background App Refresh must be on</strong> — iOS Settings, then General, then Background App Refresh. Confirm Heal and your wearable&apos;s app are both enabled.
        </li>
      </ol>
      <p>
        <strong>Heads up:</strong> overnight HRV typically lands in the morning after you wake up — not immediately. If you slept late, give it an hour.
      </p>

      <h3>The Apple Watch app won&apos;t open or isn&apos;t responding</h3>
      <ol>
        <li>
          <strong>Force-quit on the watch</strong> — hold the side button until the power slider appears. Press the digital crown to close the active app.
        </li>
        <li>
          <strong>Restart the watch</strong> — hold the side button, tap the power slider. Press and hold the side button again to turn back on.
        </li>
        <li>
          <strong>Reinstall on the watch</strong> — on your phone, open the Watch app, My Watch, scroll to Heal, toggle &ldquo;Show App on Apple Watch&rdquo; off, then on again. The app will reinstall.
        </li>
      </ol>

      <h3>Whoop or Oura disconnected</h3>
      <ol>
        <li>
          <strong>Open the wearable&apos;s app</strong> — confirm you&apos;re still logged in and data is syncing. If the wearable itself is offline, Heal can&apos;t pull data either.
        </li>
        <li>
          <strong>Reconnect in Heal</strong> — Profile, then Settings, then Wearables, then your wearable. Tap Disconnect, then Connect again. You&apos;ll be sent to the wearable&apos;s site to re-authorise — this usually fixes connection expiry.
        </li>
        <li>
          <strong>Check Wi-Fi and cellular</strong> — wearable connections sync over the internet, not Bluetooth. If your phone has no connection, no data flows.
        </li>
      </ol>

      <h3>Heal kept tracking when I started a workout in another app</h3>
      <ol>
        <li>
          <strong>Flip Heal to Soundtrack mode</strong> — when you&apos;re using another tracker, set Heal to Soundtrack — it&apos;ll stay out of the way and let your workout app do its job. Heal still pulls overnight data in the background.
        </li>
        <li>
          <strong>Check the default mode in Settings</strong> — Profile, then Settings, then Heal Mode. If you mostly use Heal as a soundtrack, set the default to Soundtrack so this doesn&apos;t happen again.
        </li>
      </ol>
      <p>
        If you noticed a duplicate workout in your wearable&apos;s app: open that app and delete the rogue session manually. Heal won&apos;t write to it again now that you&apos;ve switched modes.
      </p>

      <h2>Music &amp; audio</h2>

      <h3>Music won&apos;t play, or cuts out mid-track</h3>
      <ol>
        <li>
          <strong>Check device volume</strong> — both sides — iOS has separate volumes for ringer and media. Press volume up while audio is playing to make sure it&apos;s the media volume going up, not the ringer.
        </li>
        <li>
          <strong>Confirm your streaming subscription is active</strong> — open Apple Music or Spotify (whichever you connected). If a track plays there, the subscription is fine. If you see &ldquo;subscription required&rdquo;, that&apos;s the issue.
        </li>
        <li>
          <strong>Reconnect the streaming service</strong> — Profile, then Settings, then Music. Disconnect and reconnect. Sign-in sessions occasionally expire silently.
        </li>
        <li>
          <strong>Toggle Bluetooth / AirPods</strong> — if you&apos;re on AirPods or another Bluetooth speaker, disconnect and reconnect them. iOS Settings, then Bluetooth.
        </li>
        <li>
          <strong>Try wired headphones or the phone speaker</strong> — rules out Bluetooth entirely. If it works wired, the issue is with your audio device, not Heal.
        </li>
      </ol>

      <h3>&ldquo;Subscription required&rdquo; prompt when I tap play</h3>
      <ol>
        <li>
          <strong>Open your streaming app and confirm you&apos;re signed in</strong> — Apple Music or Spotify needs an active subscription for full track playback in Heal.
        </li>
        <li>
          <strong>Restore purchases (Heal Premium)</strong> — Profile, then Subscription, then Restore Purchases. If you&apos;ve paid but Heal doesn&apos;t see it, this fixes it.
        </li>
        <li>
          <strong>Check your Apple ID subscriptions</strong> — iOS Settings, then your name, then Subscriptions. Confirm Heal Premium is listed and active.
        </li>
        <li>
          <strong>Sign out of Heal, then back in</strong> — refreshes the subscription state on Heal&apos;s side.
        </li>
      </ol>

      <h3>Audio plays through the wrong device</h3>
      <ol>
        <li>
          <strong>Use the AirPlay picker</strong> — from Control Centre, hold the audio module and pick the right output. Or tap the AirPlay icon inside Heal&apos;s player.
        </li>
        <li>
          <strong>Disconnect other Bluetooth devices</strong> — if you have multiple speakers paired (car, home, AirPods), iOS sometimes picks the wrong one. iOS Settings, then Bluetooth, then tap the info icon next to the wrong device, then Disconnect.
        </li>
      </ol>

      <h2>Data &amp; Insights</h2>

      <h3>My data isn&apos;t showing in Insights</h3>
      <ol>
        <li>
          <strong>Pull down on the Insights screen to refresh</strong> — manual sync sometimes catches what background sync missed.
        </li>
        <li>
          <strong>Wait</strong> — most overnight data arrives in the morning — HRV, sleep stages, and recovery scores from your wearable typically land within an hour of waking. Give it time before assuming something&apos;s broken.
        </li>
        <li>
          <strong>Confirm the wearable has the data</strong> — open the wearable&apos;s own app. If it doesn&apos;t have today&apos;s data either, the issue is upstream — fix it there, then it&apos;ll flow to Heal.
        </li>
        <li>
          <strong>Check Background App Refresh is on</strong> — iOS Settings, then General, then Background App Refresh. Heal needs this on to pull overnight data while you sleep.
        </li>
      </ol>

      <h3>My HRV looks wrong, or way different from yesterday</h3>
      <ol>
        <li>
          <strong>HRV genuinely varies a lot day to day</strong> — sleep, alcohol, training, stress, illness, even the room temperature can swing HRV significantly in a healthy person. One unusual reading isn&apos;t a measurement error.
        </li>
        <li>
          <strong>Check the source matches what you expect</strong> — if you wear both an Apple Watch and a Whoop, Heal uses whichever you set as primary. Profile, then Settings, then Wearables.
        </li>
        <li>
          <strong>Compare with the wearable&apos;s own app</strong> — if Heal and the wearable show the same number, the data is correct. If they disagree, force a sync (step 1 of the section above).
        </li>
      </ol>

      <h3>Insights say &ldquo;Building your baseline&rdquo;</h3>
      <ol>
        <li>
          <strong>This is normal in your first week</strong> — Heal needs about 7 days of HRV data to learn your personal baseline. Until then, single readings can&apos;t be classified.
        </li>
        <li>
          <strong>Make sure your wearable is syncing daily</strong> — if days are missing, the baseline takes longer to build. Wear your watch consistently for the first week, especially overnight.
        </li>
      </ol>

      <h2>Notifications</h2>

      <h3>I&apos;m not getting notifications</h3>
      <ol>
        <li>
          <strong>Check iOS lets Heal notify you</strong> — iOS Settings, then Notifications, then Heal. Make sure Allow Notifications is on, and at least one of Lock Screen, Notification Centre, or Banners is enabled.
        </li>
        <li>
          <strong>Check Focus modes aren&apos;t silencing Heal</strong> — iOS Settings, then Focus, then your active Focus, then Apps. If Heal is in the silenced list, remove it.
        </li>
        <li>
          <strong>Check Heal&apos;s own notification settings</strong> — Profile, then Settings, then Notifications. Make sure morning recap, evening wind-down, and recovery prompts are all enabled.
        </li>
      </ol>

      <h3>Notifications are coming at the wrong time</h3>
      <ol>
        <li>
          <strong>Wind-down timing is learned from your usage</strong> — Heal sends the evening notification about 30 minutes before your typical session time. If you start using Heal at a new time of day, the system adjusts within a week.
        </li>
        <li>
          <strong>Set a manual wind-down window</strong> — Profile, then Settings, then Notifications, then Wind-down. Set the time you want the prompt to arrive each evening.
        </li>
      </ol>

      <h2>Account &amp; subscription</h2>

      <h3>I can&apos;t log in</h3>
      <ol>
        <li>
          <strong>Tap &ldquo;Forgot password&rdquo; on the login screen</strong> — the reset email arrives within a minute. Check your spam folder if it doesn&apos;t show up.
        </li>
        <li>
          <strong>Check caps lock and autocorrect</strong> — iOS sometimes capitalises the first character of an email field. Also try typing your password into Notes first to see it clearly, then paste it in.
        </li>
        <li>
          <strong>If you signed up with Apple or Google</strong> — use that same option on the login screen — not email and password. The two are separate accounts.
        </li>
      </ol>

      <h3>Premium features look locked even though I&apos;m subscribed</h3>
      <ol>
        <li>
          <strong>Restore purchases</strong> — Profile, then Subscription, then Restore Purchases. This is the number one fix.
        </li>
        <li>
          <strong>Confirm the subscription is active on your Apple ID</strong> — iOS Settings, then your name, then Subscriptions. Make sure Heal Premium is listed and not expired. Check the renewal date.
        </li>
        <li>
          <strong>Sign out and back in</strong> — forces Heal to re-check your subscription.
        </li>
        <li>
          <strong>Make sure you&apos;re using the right Apple ID</strong> — if you subscribed on one Apple ID and signed into Heal on another, the subscription won&apos;t transfer. iOS Settings, then your name, shows which Apple ID you&apos;re on.
        </li>
      </ol>

      <h3>How do I cancel my subscription?</h3>
      <ol>
        <li>
          <strong>Apple manages all subscriptions</strong> — iOS Settings, then your name, then Subscriptions, then Heal, then Cancel Subscription. You&apos;ll keep access until the end of your current billing period.
        </li>
        <li>
          You won&apos;t lose your data. If you cancel, your account stays — you just lose access to Premium features. Resubscribe any time and everything&apos;s where you left it.
        </li>
      </ol>

      <h2>Still stuck?</h2>
      <p>
        If you&apos;ve worked through everything and it&apos;s still not right, email{' '}
        <a href="mailto:help@healmusicapp.com">help@healmusicapp.com</a>.
        Include your phone model, iOS version, which wearable you use, and a one-line description of what&apos;s happening. We usually reply within 24 hours.
      </p>
    </div>
  </div>
);

export default Troubleshooting;
